import React, { useState, useEffect, useCallback, FormEvent } from 'react';
import { useAuth } from '@shootout-comps/core';
import { useLang } from '@shootout-comps/i18n';

// reCAPTCHA v3 site key — loaded from public settings so no rebuild needed when key changes
declare global {
  interface Window {
    grecaptcha: {
      ready: (cb: () => void) => void;
      execute: (siteKey: string, opts: { action: string }) => Promise<string>;
    };
  }
}

function useRecaptcha(siteKey: string | null) {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!siteKey) return;
    // Avoid loading the script twice
    if (document.querySelector(`script[src*="recaptcha"]`)) {
      if (window.grecaptcha) { setReady(true); return; }
    }
    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/api.js?render=${siteKey}`;
    script.async = true;
    script.onload = () => {
      window.grecaptcha.ready(() => setReady(true));
    };
    document.head.appendChild(script);
  }, [siteKey]);

  const execute = useCallback(async (action: string): Promise<string | null> => {
    if (!siteKey || !ready) return null;
    try {
      return await window.grecaptcha.execute(siteKey, { action });
    } catch {
      return null;
    }
  }, [siteKey, ready]);

  return { ready, execute };
}

export default function ContactPage() {
  const { user, getAccessToken } = useAuth();
  const { t } = useLang();

  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending]   = useState(false);
  const [sent, setSent]         = useState(false);
  const [error, setError]       = useState('');
  const [recaptchaSiteKey, setRecaptchaSiteKey] = useState<string | null>(null);

  // Fetch site key from public settings
  useEffect(() => {
    fetch('/api/settings/public')
      .then(r => r.json())
      .then((d: { recaptchaSiteKey?: string }) => {
        if (d.recaptchaSiteKey) setRecaptchaSiteKey(d.recaptchaSiteKey);
      })
      .catch(() => { /* recaptcha disabled */ });
  }, []);

  const { execute: executeRecaptcha } = useRecaptcha(recaptchaSiteKey);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!subject.trim() || !message.trim()) return;
    setSending(true); setError('');
    try {
      // Get reCAPTCHA token if configured
      const recaptchaToken = recaptchaSiteKey ? await executeRecaptcha('contact') : null;

      const token = await getAccessToken();
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          subject: subject.trim(),
          message: message.trim(),
          ...(recaptchaToken ? { recaptchaToken } : {}),
        }),
      });
      const data = await res.json() as { success?: boolean; error?: string };
      if (!res.ok) throw new Error(data.error ?? 'Failed to send message');
      setSent(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleReset = () => {
    setSent(false); setSubject(''); setMessage(''); setError('');
  };

  return (
    <div style={{ maxWidth: '600px', margin: '0 auto', padding: 'var(--page-pad)' }}>
      <h2 style={{ fontFamily: 'Bebas Neue', fontSize: '2.2rem', color: 'var(--gold)', marginBottom: '0.35rem' }}>
        {t.contactTitle}
      </h2>
      <p style={{ color: 'var(--chalk-dim)', fontSize: '0.85rem', marginBottom: '1.75rem', lineHeight: 1.6 }}>
        {t.contactSubtitle}{' '}<strong style={{ color: 'var(--chalk)' }}>{user?.email}</strong>.
      </p>

      {sent ? (
        <div style={{
          background: 'rgba(46,204,113,0.08)', border: '1px solid rgba(46,204,113,0.3)',
          borderRadius: '12px', padding: '2.5rem', textAlign: 'center',
        }}>
          <div style={{ fontSize: '2.5rem', marginBottom: '0.75rem' }}>✅</div>
          <h3 style={{ color: '#2ecc71', fontFamily: 'Bebas Neue', fontSize: '1.6rem', margin: '0 0 0.5rem' }}>
            {t.contactSentTitle}
          </h3>
          <p style={{ color: 'var(--chalk-dim)', fontSize: '0.88rem', marginBottom: '1.5rem', lineHeight: 1.6 }}>
            {t.contactSentDesc}
          </p>
          <button
            onClick={handleReset}
            style={{ background: 'transparent', border: '1px solid rgba(255,255,255,0.15)', color: 'var(--chalk-dim)', borderRadius: '7px', padding: '0.55rem 1.25rem', fontSize: '0.83rem', cursor: 'pointer' }}
          >
            {t.contactSendAnother}
          </button>
        </div>
      ) : (
        <div style={{
          background: 'linear-gradient(135deg, rgba(255,255,255,0.05), rgba(255,255,255,0.02))',
          border: '1px solid rgba(255,255,255,0.09)', borderRadius: '12px', padding: '1.75rem',
        }}>
          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {/* From — read-only, informational */}
            <div>
              <label style={labelStyle}>{t.fromLabel}</label>
              <div style={{
                padding: '0.55rem 0.85rem', background: 'rgba(0,0,0,0.2)',
                border: '1.5px solid rgba(255,255,255,0.08)', borderRadius: '7px',
                color: 'var(--chalk-dim)', fontSize: '0.85rem',
              }}>
                {user?.username} &lt;{user?.email}&gt;
              </div>
            </div>

            {/* Subject */}
            <div>
              <label style={labelStyle}>{t.contactSubjectLabel}</label>
              <input
                value={subject}
                onChange={e => setSubject(e.target.value)}
                placeholder="e.g. Question about tournament formats"
                maxLength={200}
                required
                style={inputStyle}
              />
              <div style={{ fontSize: '0.68rem', color: 'var(--chalk-dim)', marginTop: '0.25rem', textAlign: 'right' }}>
                {subject.length}/200
              </div>
            </div>

            {/* Message */}
            <div>
              <label style={labelStyle}>{t.contactMessageLabel}</label>
              <textarea
                value={message}
                onChange={e => setMessage(e.target.value)}
                placeholder="Tell us what's on your mind…"
                maxLength={5000}
                required
                rows={7}
                style={{ ...inputStyle, resize: 'vertical', minHeight: '140px', lineHeight: 1.6 }}
              />
              <div style={{ fontSize: '0.68rem', color: 'var(--chalk-dim)', marginTop: '0.25rem', textAlign: 'right' }}>
                {message.length}/5000
              </div>
            </div>

            {error && (
              <div style={{
                background: 'rgba(192,57,43,0.12)', border: '1px solid rgba(192,57,43,0.3)',
                borderRadius: '6px', padding: '0.6rem 0.85rem',
                color: '#e74c3c', fontSize: '0.82rem',
              }}>
                {error}
              </div>
            )}

            {/* reCAPTCHA badge notice */}
            {recaptchaSiteKey && (
              <p style={{ fontSize: '0.68rem', color: 'var(--chalk-dim)', margin: 0, lineHeight: 1.5 }}>
                This form is protected by reCAPTCHA.{' '}
                <a href="https://policies.google.com/privacy" target="_blank" rel="noreferrer" style={{ color: 'var(--chalk-dim)' }}>Privacy Policy</a>{' '}
                &amp;{' '}
                <a href="https://policies.google.com/terms" target="_blank" rel="noreferrer" style={{ color: 'var(--chalk-dim)' }}>Terms of Service</a>{' '}
                apply.
              </p>
            )}

            <button
              type="submit"
              disabled={sending || !subject.trim() || !message.trim()}
              style={{
                background: 'linear-gradient(135deg, var(--gold), var(--gold-light))',
                color: 'var(--baize-dark)', border: 'none', borderRadius: '8px',
                padding: '0.85rem', fontSize: '0.95rem', fontWeight: 700,
                letterSpacing: '0.06em', textTransform: 'uppercase',
                cursor: sending || !subject.trim() || !message.trim() ? 'not-allowed' : 'pointer',
                opacity: sending || !subject.trim() || !message.trim() ? 0.5 : 1,
                minHeight: '48px', transition: 'opacity 0.2s',
              }}
            >
              {sending ? t.saving : t.contactSendBtn}
            </button>

            <p style={{ textAlign: 'center', color: 'var(--chalk-dim)', fontSize: '0.73rem', margin: 0 }}>
              {t.contactAimToRespond}
            </p>
          </form>
        </div>
      )}
    </div>
  );
}

const labelStyle: React.CSSProperties = {
  fontSize: '0.72rem', color: 'var(--chalk-dim)', textTransform: 'uppercase',
  letterSpacing: '0.1em', display: 'block', marginBottom: '0.35rem', fontWeight: 600,
};

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '0.65rem 0.85rem',
  background: 'rgba(0,0,0,0.3)', border: '1.5px solid rgba(201,168,76,0.25)',
  borderRadius: '7px', color: 'var(--chalk)', fontSize: '0.88rem',
  outline: 'none', boxSizing: 'border-box',
};
